<template>
    <v-window v-model="wizardStep" touchless>
        <v-window-item :value="0" class="pa-0">
            <v-row v-if="showSwitchs" class="pt-1 mx-2 justify-center" style="min-width: 550px">
                <v-col cols="3">
                    <v-switch
                        v-model="noHeaders"
                        label="Sans en-têtes"
                        hide-details
                        color="primary"
                        density="compact"
                    />
                </v-col>
                <v-col cols="4">
                    <oss-text-field-filled
                        v-model="startLine"
                        :rules="[(v) => v < maxStartLine]"
                        :disabled="!noHeaders"
                        hide-details
                        density="compact"
                        label="Ligne de début"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" class="px-2 justify-center">
                    <v-data-table
                        density="compact"
                        :headers="headersCorrespondance"
                        :items="itemsCorrespondance"
                        hide-default-footer
                        :items-per-page="-1"
                        class="table"
                        fixed-header
                    >
                        <template #headers>
                            <tr>
                                <th
                                    v-for="(header, index) in headersCorrespondance"
                                    :key="index"
                                    :style="getHeaderStyle()"
                                >
                                    {{ header.text }}
                                </th>
                            </tr>
                        </template>
                        <template v-slot:item="{ item, index }">
                            <tr :style="getRowStyle(index)">
                                <td v-for="(wording, windex) in item" :key="index+'-'+windex">
                                    {{ wording }}
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-window-item>
        <v-window-item :value="1">
            <v-row v-if="!allDatesValid && requireDateVerification" no-gutters>
                <v-col cols="12">
                    <v-alert dense type="warning">
                        <v-row class="align-center">
                            <v-col class="grow">
                                <div>Certaines dates sont dans un exercice clôturé</div>
                                <div style="font-size: 12px">
                                    Si vous continuez, ces transactions bancaires seront supprimées.
                                </div>
                            </v-col>
                            <!-- <v-col v-if="validateAccountingDates" class="shrink">
                                <v-btn
                                    @click="someDatesInvalid = false;"
                                    style="background-color: #fb8e08"
                                    rounded
                                    outlined
                                    elevation="0"
                                    small
                                >Valider les dates</v-btn>
                            </v-col> -->
                        </v-row>
                    </v-alert>
                </v-col>
            </v-row>
            <v-row v-if="foundErrors.length" no-gutters>
                <v-col cols="12">
                    <v-alert density="compact" type="warning" :text="errorDescription" />
                </v-col>
            </v-row>
            <v-row no-gutters class="pt-2">
                <v-col
                    :cols="requireDateVerification && updateAccountingDates ? 10 : 12"
                    class="d-flex justify-start flex-wrap"
                >
                    <v-chip variant="text" density="compact" class="mb-1">
                        Données requises :
                    </v-chip>
                    <v-chip
                        v-for="(item, index) in formatColumns.filter(
                            (c) => c.mandatory && !c.pair && !c.optional,
                        )"
                        :color="positions[item.value] ? 'success' : 'error'"
                        density="compact"
                        variant="outlined"
                        v-bind:key="index"
                        style="margin-left: 4px"
                        class="mb-1"
                    >
                        {{ item.text }}
                    </v-chip>
                    <v-chip
                        v-for="(item, index) in formatColumns.filter((c) => c.pair)"
                        :color="
                            positions[item.value] ? 'success' : item.optional ? 'orange' : 'error'
                        "
                        :style="columnOptions('pairs', item) ? '' : 'display:none'"
                        density="compact"
                        variant="outlined"
                        v-bind:key="'A' + index"
                        style="margin-left: 4px"
                        class="mb-1"
                    >
                        {{ item.text }}
                    </v-chip>
                    <!-- At least two selected -->
                    <v-chip
                        v-for="(item, index) in formatColumns.filter(
                            (c) => c.optional && c.mandatory,
                        )"
                        :color="
                            positions[item.value]
                                ? 'success'
                                : columnOptions('selection', item)
                                  ? 'orange'
                                  : 'error'
                        "
                        density="compact"
                        variant="outlined"
                        v-bind:key="'B' + index"
                        style="margin-left: 4px"
                        class="mb-1"
                    >
                        {{ item.text }}
                    </v-chip>
                    <v-chip
                        v-for="(item, index) in formatColumns.filter(
                            (c) => c.optional && !c.pair && !c.mandatory,
                        )"
                        :color="positions[item.value] ? 'success' : 'orange'"
                        density="compact"
                        variant="outlined"
                        v-bind:key="'C' + index"
                        style="margin-left: 4px"
                        class="mb-1"
                    >
                        {{ item.text }}
                    </v-chip>
                </v-col>
                <v-col
                    v-if="requireDateVerification && updateAccountingDates"
                    cols="2"
                    class="pr-1 d-flex justify-end align-center"
                >
                    <v-btn
                        class="btn"
                        rounded
                        size="x-small"
                        color="secondary"
                        @click="updateDates()"
                        :disabled="correctDatesAndHeaders()"
                    >
                        Mise à jour des dates
                    </v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" class="px-2 justify-center">
                    <oss-data-table
                        :headers="headersCorrespondance"
                        :items="itemsCorrespondance"
                        :rowStyles="invalidDates"
                        :fixed-header="true"
                        :fixed-footer="true"
                        :striped="true"
                        :x-dense="true"
                    >
                        <template v-slot:header.append>
                            <tr>
                                <th
                                    class="header"
                                    style="top: 26px; font-weight: normal"
                                    v-for="(header, index) in headersCorrespondance"
                                    :key="index"
                                >
                                    <v-select-filled
                                        v-if="
                                            headersCorrespondance[index].value !== 'accountingDate'
                                        "
                                        v-model="itemsSelection[index].type"
                                        :items="formatColumns"
                                        item-title="text"
                                        item-value="value"
                                        hide-details
                                        min-width="100px"
                                        density="compact"
                                        @update:model-value="onItemsChanged(itemsSelection[index])"
                                    />
                                </th>
                            </tr>
                        </template>
                    </oss-data-table>
                </v-col>
            </v-row>
        </v-window-item>
    </v-window>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, toNative } from "vue-facing-decorator";
import moment from "moment-timezone";

import csvtojson from "csvtojson";

import OssDataTable from "../ossComponents/ossDataTable2.vue";
//Date verification
import { isInsideClosedAccountingPeriod } from "../../services/accountingPeriods";
import { getAccountingDate } from "../../services/accountingPeriods";

//Amount verification
import { checkAmount } from "../../services/mathServices";
import { getDBFormattedDate } from "@/services/dateServices";

type Tuple = [RegExp, number];

interface ProcessResult {
    rawData: any;
    preProcessItemCorrespondence: any;
    preProcessHeaderCorrespondence: any;
    preProcessItemSelection: any;
    processedData: any;
    success: boolean;
}

@Component({
    components: {
        OssDataTable,
    },
    emits: ["new-document", "columns-chosen", "back"],
})
export class ColumnCorrespondence extends Vue {
    @Prop() actions: any;
    @Prop({ default: false }) readonly ignoreHeaders: boolean;
    @Prop({ required: true, type: Object }) result!: any;
    @Prop({ required: true, type: String }) extension!: string;
    @Prop({ default: null }) formatColumns: any;
    @Prop({ default: null }) ruleManager: any;
    @Prop({ default: null }) columnMapping: any;
    @Prop({ default: false }) resultIsArray: boolean;
    @Prop({ default: false }) requireDateVerification: boolean;
    @Prop({ default: false }) updateAccountingDates: boolean;
    @Prop({ default: false }) validateAccountingDates: boolean;
    @Prop({ default: "date" }) dateVerifWording: string;
    @Prop({ default: true }) showSwitchs: boolean;
    @Prop({
        type: Function,
        required: false,
        default: () => ({
            success: false,
            rawData: undefined,
            preProcessItemCorrespondence: undefined,
            preProcessHeaderCorrespondence: undefined,
            preProcessItemSelection: undefined,
            processedData: undefined,
        }),
    })
    preProcessFunction!: (...args: any[]) => ProcessResult;

    // Date format
    dateRegEx: Array<RegExp> = [
        /^(((0[13-9]|1[012])[-/]?(0[1-9]|[12][0-9]|30)|(0[13578]|1[02])[-/]?31|02[-/]?(0[1-9]|1[0-9]|2[0-8]))[-/]?[0-9]{4}|02[-/]?29[-/]?([0-9]{2}(([2468][048]|[02468][48])|[13579][26])|([13579][26]|[02468][048]|0[0-9]|1[0-6])00))$/gi,
        /^(((0[1-9]|[12][0-9]|30)[-/]?(0[13-9]|1[012])|31[-/]?(0[13578]|1[02])|(0[1-9]|1[0-9]|2[0-8])[-/]?02)[-/]?[0-9]{4}|29[-/]?02[-/]?([0-9]{2}(([2468][048]|[02468][48])|[13579][26])|([13579][26]|[02468][048]|0[0-9]|1[0-6])00))$/gi,
        /^([0-9]{4}[-/]?((0[13-9]|1[012])[-/]?(0[1-9]|[12][0-9]|30)|(0[13578]|1[02])[-/]?31|02[-/]?(0[1-9]|1[0-9]|2[0-8]))|([0-9]{2}(([2468][048]|[02468][48])|[13579][26])|([13579][26]|[02468][048]|0[0-9]|1[0-6])00)[-/]?02[-/]?29)$/gi,
    ];

    wizardStep = 0;

    foundErrors = [];

    // Preprocessing
    noHeaders = false;
    startLine = 0;
    maxStartLine = 0;
    analysedData: any = null;

    // Table content
    stream = null;
    items: Array<any> = [];
    itemsCorrespondance: Array<any> = [];
    headersCorrespondance: Array<any> = [];
    itemsSelection: Array<any> = [];
    rawData: Array<any> = [];
    separator: string = "";

    // Date processing
    invalidDates: Array<any> = [];
    accDates: Array<any> = [];
    allDatesValid: boolean = true;
    // someDatesInvalid:boolean = true;

    get positions() {
        let headers = this.formatColumns.map((x) => x.value);
        headers.shift(); // Remove empty item

        let positions = headers.reduce((a, v) => ({ ...a, [v]: false }), {});
        for (const item of this.itemsSelection) {
            const match = headers.find((h) => h === item.type);
            if (match) {
                positions[match] = true;
            }
        }
        return positions;
    }

    get headers() {
        let headers = this.formatColumns
            .filter((c) => c.caption)
            .map((c) => {
                return { title: c.text, value: c.value, align: "center", sortable: false };
            });
        headers.push({
            title: "Information",
            value: "information",
            align: "center",
            sortable: false,
        });
        return headers;
    }

    get errorDescription() {
        if (!this.foundErrors.length) {
            return "";
        }
        let description = "";
        for (const error of this.foundErrors) {
            if (description) description += " - ";
            description += error.description + " (" + error.count + ")";
        }
        return description;
    }

    beforeMount() {
        this.setActionButton();
        this.readData(this.result.data, this.result.file);
        if (this.columnMapping?.length) {
            this.wizardStep = 1;
        }
        // if (this.extension === "csv") {
        //     this.wizardStep = 0;
        // } else {
        //     this.wizardStep = 1;
        // }
    }

    get allColumnsPresent() {
        const mandatory = this.formatColumns.filter((c) => c.mandatory && !c.pair && !c.optional);
        for (const header of mandatory) {
            if (!this.positions[header.value]) {
                return false;
            }
        }

        let pairsComplete = false;
        const pairs = this.formatColumns.filter((c) => c.pair && c.mandatory);
        for (const header of pairs) {
            if (
                this.positions[header.value] &&
                (header.pair === "none" ||
                    this.positions[header.pair] ||
                    this.formatColumns.find((h) => h.value === header.pair)?.optional)
            ) {
                pairsComplete = true;
                break;
            }
        }
        if (!pairsComplete && pairs.length) {
            return false;
        }

        const selection = this.formatColumns.filter((c) => c.optional && c.mandatory);
        if (selection.length) {
            if (selection.filter((c) => this.positions[c.value]).length < 2) {
                return false;
            }
        }

        return true;
    }

    columnOptions(caseChoice, item) {
        if (caseChoice === "pairs") {
            const pairs = this.formatColumns.filter((c) => c.pair && c.mandatory);
            if (this.positions[item.value] || this.positions[item.pair]) {
                // Priority to first pair if both selected
                if (this.positions[pairs[0].value] || this.positions[pairs[0].pair]) {
                    if (item.value === pairs[0].value || item.value === pairs[0].pair) {
                        return true;
                    }
                    return false;
                }
                return true;
            }
            const chosenItems = this.formatColumns.filter((c) => c.pair && this.positions[c.value]);
            if (!chosenItems.length) {
                if (item?.value === pairs[0]?.value || item?.value === pairs[0]?.pair) {
                    return true;
                }
            }
        } else if (caseChoice === "selection") {
            const chosenItems = this.formatColumns.filter(
                (c) => c.optional && c.mandatory && this.positions[c.value],
            );
            if (chosenItems.length >= 2) {
                return true;
            }
        }
        return false;
    }

    correctDatesAndHeaders() {
        if (!this.allColumnsPresent && !this.allDatesValid) {
            return true;
        }
        if (this.allDatesValid) {
            return true;
        }
        return false;
    }

    onNewDocument() {
        this.$emit("new-document");
    }

    resetVariables() {
        this.rawData = [];
        this.headersCorrespondance = [];
        this.itemsCorrespondance = [];
        this.itemsSelection = [];
        this.invalidDates = [];
        this.foundErrors = [];
    }

    cleanUnicode(arrayLines: Array<any>) {
        const cleanLines = arrayLines.map((line) => line.replaceAll(/\0/g, "")); //Delete null unicode characters
        if (/[^\u0000-\u00ff]/.test(cleanLines[0])) {
            console.log("Warning: Non-ASCII characters detected");
        }
        return cleanLines;
    }

    async readData(result, file) {
        let buffer: Array<any>;
        if (this.resultIsArray) {
            buffer = this.cleanUnicode(result);
        } else {
            let lines = this.cleanUnicode(result.split(/(?:\r\n?|\n)/));
            lines[0] = lines[0].replace(/[.]/, "");
            buffer = (lines.join("\n") + "")
                .split(/(?:\r\n?|\n)/)
                .filter((line) => line.replace(/ /g, "").length);
        }

        if (this.separator === "" || file.name.toString() !== this.result.file.name) {
            let lineForseparatorDetection: number = buffer.length - 1;
            while (buffer[lineForseparatorDetection].length === 0) {
                lineForseparatorDetection -= 1;
            }
            this.separator = this.separatorDetection(buffer[lineForseparatorDetection]);
            if (this.separator === "\\t") {
                this.separator = "\t";
            } else if (this.separator === "\\|") {
                this.separator = "\|";
            }
        }
        const { headers, deductedHeaders, formatResult } = this.prepareBuffer(buffer);

        this.resetVariables();
        this.stream = formatResult;
        this.itemsCorrespondance = await this.getJson(formatResult, headers);
        this.headerConstruction(headers, deductedHeaders);

        const someD = this.itemsCorrespondance.some((i) => i.M && i.M === "D");
        if (someD) {
            this.formatColumns.find(
                (c) => c.value === "previousDerogationDepreciationAmountSigned",
            ).mandatory = true;
            this.formatColumns.find((c) => c.value === "aaNameDerogation").mandatory = true;
        }

        this.maxStartLine = buffer.length;
        if (this.startLine) {
            this.itemsCorrespondance.splice(0, this.startLine);
        }
        this.rawData.push(Object.keys(this.itemsCorrespondance[0]).join(this.separator));
        const joinString = '"' + this.separator + '"';
        for (const row of this.itemsCorrespondance) {
            this.rawData.push('"' + Object.values(row).join(joinString) + '"');
        }

        this.preProcessData(headers);

        if (this.requireDateVerification) {
            const dateColumn = this.itemsSelection.find((x) => x.type === this.dateVerifWording);
            if (dateColumn) {
                this.checkDates(dateColumn.import);
            }
        }
    }

    cleanBuffer(buffer) {
        let cbuffer = buffer.filter((row) => row !== "");
        cbuffer = cbuffer.filter((row) =>
            row.split(this.separator).some((value) => value.trim() !== ""),
        );
        return cbuffer;
    }

    cleanWord(word) {
        let cw = word.replace(/^ {1,}/, "");
        cw = cw.replace(/ {1,}$/, "");
        return cw;
    }

    separatorDetection(line: string) {
        const separators: Array<RegExp> = [/;/gm, /,/gm, /\t/gm, /\|/gm]; // Possible list of separators
        let separator: Tuple = [/ /g, 0];

        // research separator
        separators.forEach((sep) => {
            const numbOcc = (line.match(sep) || []).length;
            if (numbOcc >= separator[1]) {
                separator = [sep, numbOcc];
            }
        });
        // convert to string
        return separator[0].source;
    }

    headerConstruction(headers, deductedHeaders) {
        for (const index in headers) {
            const header = headers[index];

            this.headersCorrespondance.push({
                text: header,
                value: header,
                align: "center",
                class: "font-weight-bold",
            });
            let type = deductedHeaders[index] || "";
            if (this.columnMapping?.length) {
                type = this.columnMapping[index].type;
            }
            this.itemsSelection.push({
                type: type,
                value: "v" + index,
                import: header,
            });
        }
    }

    checkDates(value) {
        this.allDatesValid = true;
        if (this.positions[this.dateVerifWording]) {
            for (let i = 0; i < this.itemsCorrespondance.length; i++) {
                const word = this.itemsCorrespondance[i][value];
                if (word) {
                    if (
                        word.match(this.dateRegEx[0]) ||
                        word.match(this.dateRegEx[1]) ||
                        word.match(this.dateRegEx[2])
                    ) {
                        let standardDate = getDBFormattedDate(this.toDate(word));
                        if (isInsideClosedAccountingPeriod(this.$store, standardDate)) {
                            this.invalidDates[i] = "background-color: rgb(253, 2, 2, 0.2)";
                            this.allDatesValid = false;
                            // this.someDatesInvalid = true
                        } else {
                            this.invalidDates[i] = "";
                        }
                    }
                }
            }
        }
    }

    toDate(string: string) {
        // debugger
        const result = moment(string, [
            "DD/MM/YYYY",
            "DD-MM-YYYY",
            "YYYY/MM/DD",
            "YYYY-MM-DD",
            "DDMMYYYY",
        ]);
        if (!result.isValid()) {
            debugger;
        }
        return result;
    }

    updateDates() {
        this.accDates.length = 0;
        if (!this.headersCorrespondance.find((h) => h.value === "accountingDate")) {
            this.headersCorrespondance.push({
                title: "Accounting Date",
                value: "accountingDate",
                align: "center",
                class: "font-weight-bold",
            });
            this.itemsSelection.push({
                import: "Accounting Date",
                type: "accountingDate",
                value: "v" + this.itemsSelection.length,
            });
            this.positions.accountingDate = true;
        }
        for (let i = 0; i < this.itemsCorrespondance.length; i++) {
            if (this.invalidDates[i] !== "") {
                const dateColumn = this.itemsSelection.find(
                    (x) => x.type === this.dateVerifWording,
                );
                const line = this.itemsCorrespondance[i];
                const word = line[dateColumn.import];
                if (word) {
                    if (
                        word.match(this.dateRegEx[0]) ||
                        word.match(this.dateRegEx[1]) ||
                        word.match(this.dateRegEx[2])
                    ) {
                        let standardDate = this.toDate(word).format();
                        let accDate = getAccountingDate(this.$store, standardDate);
                        this.accDates.push(accDate);
                        this.itemsCorrespondance[i]["accountingDate"] =
                            moment(accDate).format("DD/MM/YYYY");
                        this.invalidDates[i] = "background-color: rgb(9, 184, 3, 0.2)";
                    }
                }
            } else {
                this.itemsCorrespondance[i]["accountingDate"] = "";
                this.accDates.push("");
            }
        }
        this.allDatesValid = true;
    }

    deductItemTypes(headers) {
        let deductedNames = [];
        for (const header of headers) {
            let deducted = this.ruleManager.deductTypeFromHeader(header);
            if (deducted && !deductedNames.includes(deducted.column)) {
                deductedNames.push(deducted.column);
            } else {
                // deductedNames.push(header);
                deductedNames.push("");
            }
        }
        return deductedNames;
    }

    onItemsChanged(item) {
        if (item.type) {
            for (let i = 0; i < this.itemsSelection.length; i++) {
                if ("v" + i !== item.value) {
                    if (this.itemsSelection[i].type === item.type) {
                        this.itemsSelection[i].type = "";
                    }
                }
            }
        }
        if (this.requireDateVerification) {
            this.allDatesValid = true;
            const dateColumn = this.itemsSelection.find((x) => x.type === this.dateVerifWording);
            if (dateColumn) {
                this.checkDates(dateColumn.import);
            } else {
                for (let i = 0; i < this.itemsCorrespondance.length; i++) {
                    this.invalidDates[i] = "";
                    if (this.headersCorrespondance.find((h) => h.value === "accountingDate")) {
                        this.headersCorrespondance.pop();
                    }
                }
            }
        }
    }

    async processData() {
        let newFormat = [];
        for (const item of this.itemsSelection) {
            newFormat.push(item.type || item.import);
        }
        this.rawData.splice(0, 1, newFormat.join(this.separator));
        const datas = this.rawData.join("\n");
        this.items = await this.getJson(datas, []);

        this.columnTypeVerification();

        if (this.requireDateVerification && this.updateAccountingDates) {
            if (this.accDates.length) {
                this.headers.splice(6, 0, {
                    text: "Date comptable",
                    value: "accountingDate",
                    align: "center",
                });
                for (let i = 0; i < this.items.length; i++) {
                    this.items[i].accountingDate = this.accDates[i] ? this.accDates[i] : undefined;
                }
            } else {
                for (let i = 0; i < this.items.length; i++) {
                    this.items[i].accountingDate = undefined;
                }
            }
        }

        const chosenData = {
            fileName: this.result.file.name,
            stream: this.stream,
            importedItems: this.items,
            importedHeaders: this.headers,
            importedAccountingDates: this.accDates,
            foundErrors: this.foundErrors,
            importedSelection: this.itemsSelection,
            positions: this.positions,
            analysedData: this.analysedData,
        };
        //if(!this.foundErrors.length){
        
        this.$emit("columns-chosen", chosenData);
        //}
    }

    prepareBuffer(buffer) {
        buffer = this.cleanBuffer(buffer);
        const firstLine = buffer[0];
        let headers = firstLine
            .split(this.separator)
            .filter((c) => !!c)
            .map((h) => {
                return this.cleanWord(h);
            });
        const deductedHeaders = this.deductItemTypes(headers);
        const noHeaders =
            (this.ignoreHeaders && headers.length) || (this.noHeaders && this.wizardStep !== 1);
        let formatResult = buffer.join("\n");

        if (!noHeaders) {
            const repeatedHeaders = new Set(headers).size !== headers.length;
            if (repeatedHeaders) {
                let counts = {};
                let result = [];

                headers.forEach((item) => {
                    counts[item] = (counts[item] || 0) + 1;
                });

                headers.forEach((item) => {
                    if (counts[item] > 1) {
                        let newItem = item;
                        let occurrenceCount = 1;
                        while (result.includes(newItem)) {
                            newItem = `${item}_${occurrenceCount}`;
                            occurrenceCount++;
                        }
                        result.push(newItem);
                    } else {
                        result.push(item);
                    }
                });
                headers = result;
                buffer[0] = headers.join(this.separator);
                formatResult = buffer.join("\n");
            }
        }
        if(this.noHeaders  && this.wizardStep !== 1) {
            headers = []
        }

        return {
            headers: headers,
            deductedHeaders: deductedHeaders,
            formatResult: formatResult,
        };
    }

    columnTypeVerification() {
        const amountPositions = [
            "unitPrice",
            "grossAmount",
            "netAmount",
            "vatAmount",
            "amount",
            "debit",
            "credit",
            "Debit",
            "Credit",
            "alNetAmountSigned",
            "alVatAmountSigned",
            "depreciableAmountSigned",
            "previousDepreciationAmountSigned",
            "previousDerogationDepreciationAmountSigned",
        ];
        const amountItems = amountPositions.filter((i) =>
            Object.keys(this.positions)
                .filter((col) => this.positions[col])
                .includes(i),
        );
        const datePositions = ["date", "dateRealization", "linearSpreadStartDate"];
        const dateItems = datePositions.filter((i) =>
            Object.keys(this.positions)
                .filter((col) => this.positions[col])
                .includes(i),
        );

        this.foundErrors = [];
        for (const item of this.items) {
            for (const amountType of amountItems) {
                if (item[amountType]) {
                    if (item[amountType].includes(",") && item[amountType].includes(".")) {
                        item[amountType] = item[amountType].replace(",", "");
                    }
                    item[amountType] = item[amountType]
                        .replace(/[$€£ ]?/g, "")
                        .replace(",", ".")
                        .replace(" ", "");
                    // debugger
                    if (!checkAmount(item[amountType])) {
                        this.addError(
                            "amount",
                            "Le montant " + item[amountType] + " n'est pas au bon format",
                        );
                    }
                }
            }
            for (const dateType of dateItems) {
                if (item[dateType]) {
                    const momentDate = this.toDate(item[dateType]);
                    if (!momentDate.isValid())
                        this.addError("date", "La date n'est pas au bon format");
                }
            }
        }
    }

    addError(key, description) {
        const error = this.foundErrors.find((e) => e.key === key);
        if (error) {
            error.count++;
        } else {
            this.foundErrors.push({
                key,
                description,
                count: 1,
            });
        }
    }

    cleanNoHeaderResult(result, fileHeader) {
        const keys = []
        const maxFields = Math.max(...result.map(obj => Object.keys(obj).length));
        for (let i = 0; i < maxFields; i++) {
            keys[i] = "v" + i;
        }
        // Remove empty fields
        const emptyFields = keys.filter(field =>
            result.every(obj => !obj[field])
        );
        const cleanedData = result.map(obj => {
            emptyFields.forEach(field => delete obj[field]);
            return obj;
        });

        for (let i = 0; i < maxFields - emptyFields.length; i++) {
            fileHeader[i] = "v" + i;
        }

        return cleanedData
    }

    async getJson(str: string, fileHeader) {
        const dicoHeaders = {};
        const noHeadersFirstStep = this.noHeaders && this.wizardStep !== 1;
        const noHeaders = (this.ignoreHeaders && fileHeader.length) || noHeadersFirstStep;
        let result = await csvtojson({
            noheader: noHeaders,
            trim: true,
            headers: noHeaders
                ? [
                      "v0",
                      "v1",
                      "v2",
                      "v3",
                      "v4",
                      "v5",
                      "v6",
                      "v7",
                      "v8",
                      "v9",
                      "v10",
                      "v11",
                      "v12",
                      "v13",
                      "v14",
                      "v15",
                  ]
                : undefined,
            delimiter: this.separator,
        }).fromString(str);
        result = result.filter((x) => Object.values(x).join(""));

        if ((fileHeader.length || noHeadersFirstStep) && !this.ignoreHeaders) {
            //Clean Data
            if (noHeadersFirstStep && result.length) {
                result = this.cleanNoHeaderResult(result, fileHeader)
            }
            for (const h of fileHeader) {
                if (h) {
                    dicoHeaders[h] = true;
                }
            }
            for (const row of result) {
                // if (Object.values(row).join("") === "") {
                //     delete result[row]
                // }
                for (const key in row) {
                    if (!dicoHeaders[key]) {
                        delete row[key];
                    }
                }
            }
        }
        return result;
    }

    /* --- Traitement des données --- */

    preProcessData(headers) {
        const result = this.preProcessFunction(headers, this.itemsCorrespondance, this.separator);
        const { success } = result;
        if (success) {
            const {
                preProcessItemCorrespondence,
                preProcessHeaderCorrespondence,
                preProcessItemSelection,
            } = result;
            const { processedData, rawData } = result;
            if (preProcessItemCorrespondence) {
                this.itemsCorrespondance = preProcessItemCorrespondence;
            }
            if (preProcessHeaderCorrespondence) {
                this.headersCorrespondance = preProcessHeaderCorrespondence;
            }
            if (preProcessItemSelection) {
                this.itemsSelection = preProcessItemSelection;
            }
            if (rawData) {
                this.rawData = rawData;
            }
            if (processedData) {
                this.analysedData = processedData;
            }
        }
    }

    @Watch("wizardStep")
    setActionButton() {
        if (this.actions && this.actions.action) {
            if (this.wizardStep === 0) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                this.actions.action.disabled = () => {
                    return this.maxStartLine < this.startLine;
                };
                this.actions.action.function = async () => {
                    await this.previewData();
                    this.wizardStep = 1;
                };
                this.actions.action.previous = () => {
                    this.$emit("back");
                };
            } else if (this.wizardStep === 1) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                this.actions.action.disabled = () => {
                    return !!!(
                        this.allColumnsPresent &&
                        !this.foundErrors.length &&
                        (this.allDatesValid || this.validateAccountingDates)
                    );
                };
                this.actions.action.function = async () => {
                    this.processData();
                };
                this.actions.action.previous = () => {
                    this.extension === "csv" ? (this.wizardStep = 0) : this.$emit("back");
                };
            }
        }
    }

    getHeaderStyle() {
        return "style=fontWeight: bold; color: black; text-align: center; border-bottom: 1px solid grey";
    }

    getRowStyle(index) {
        if (index < this.startLine) {
            return "backgroundColor: #eeeeee; pointerEvents: none; color: grey";
        }
        return "backgroundColor: transparent";
    }

    @Watch("noHeaders")
    async previewData() {
        if (!this.noHeaders) {
            this.startLine = 0;
        }
        if (this.result?.data && this.result?.file) {
            await this.readData(this.result.data, this.result.file);
        }
    }
}

export default toNative(ColumnCorrespondence);
</script>

<style lang="scss" scoped>
.v-select-font-size {
    font-size: 12px;
}

.v-slide-group__next,
.v-slide-group__prev {
    flex: 0 1 auto;
    min-width: 20px;
}
</style>
